import { useRef, useState } from 'react';
import { Clock, GripVertical, Mail, Plus, X } from 'lucide-react';

/**
 * The follow-up emails that go out after one kind of session.
 *
 * Her post-session email already lands when the session ends. This is the rest of the arc — a
 * check-in two weeks later, an invitation to come back in a month (owner, Jun 2026). Purpose
 * matters: a `rebook` invitation is dropped if the client has already booked again, while a
 * `check_in` still goes, because they booked, they did not heal.
 */
const PURPOSES = [
  { value: 'check_in', label: 'Check in on how they are doing' },
  { value: 'rebook', label: 'Invite them to book again' },
];

const blank = () => ({
  id: `fu${Date.now()}${Math.random().toString(36).slice(2, 5)}`,
  purpose: 'check_in',
  after_days: 14,
  on_date: null,
  subject: '',
  text: '',
  enabled: true,
});

export default function SessionFollowupsEditor({ followups, onChange, testPrefix }) {
  const list = followups || [];
  const [dragFrom, setDragFrom] = useState(null);
  const dragging = useRef(false);

  const update = (i, patch) => onChange(list.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  const add = () => onChange([...list, blank()]);
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));

  const drop = (to) => {
    if (dragFrom === null || dragFrom === to) return;
    const next = [...list];
    const [moved] = next.splice(dragFrom, 1);
    next.splice(to, 0, moved);
    onChange(next);
    setDragFrom(null);
    dragging.current = false;
  };

  return (
    <div data-testid={`${testPrefix}-followups`}>
      <p style={{ fontSize: 11, color: 'var(--ink-light)', margin: '0 0 8px' }}>
        These go out after the session, in your words. Use <code>{'{client_name}'}</code> and{' '}
        <code>{'{session}'}</code> and they will be filled in. An invitation to book again is
        skipped if they have already booked — a check-in is not.
      </p>

      {list.map((f, i) => (
        <div key={f.id}
          style={{
            border: '1px solid rgba(0,0,0,0.1)', borderRadius: 8, padding: 10, marginBottom: 8,
            display: 'flex', flexDirection: 'column', gap: 6,
            opacity: f.enabled === false ? 0.6 : 1,
          }}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); drop(i); }}
          data-testid={`${testPrefix}-followup-${i}`}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span draggable
              onDragStart={() => { setDragFrom(i); dragging.current = true; }}
              onDragEnd={() => { setDragFrom(null); dragging.current = false; }}
              style={{ cursor: 'grab', color: 'var(--ink-light)', display: 'flex' }}
              title="Drag to reorder">
              <GripVertical size={14} />
            </span>
            <Mail size={13} style={{ opacity: 0.6 }} />
            <select
              className="vav-session-input"
              value={f.purpose || 'check_in'}
              onChange={(e) => update(i, { purpose: e.target.value })}
              style={{ flex: 1, maxWidth: 260 }}
              data-testid={`${testPrefix}-followup-purpose-${i}`}
            >
              {PURPOSES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
            <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, cursor: 'pointer' }}>
              <input type="checkbox" checked={f.enabled !== false}
                onChange={(e) => update(i, { enabled: e.target.checked })}
                data-testid={`${testPrefix}-followup-enabled-${i}`} />
              On
            </label>
            <button className="vav-remove-block" onClick={() => remove(i)}
              data-testid={`${testPrefix}-followup-remove-${i}`}>
              <X size={13} />
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12 }}>
            <Clock size={13} style={{ opacity: 0.6 }} />
            {f.on_date ? (
              <>
                <span>on</span>
                <input className="vav-session-input" type="date" style={{ width: 160 }}
                  value={f.on_date || ''}
                  onChange={(e) => update(i, { on_date: e.target.value || null })}
                  data-testid={`${testPrefix}-followup-date-${i}`} />
                <button className="vav-add-block" onClick={() => update(i, { on_date: null })}
                  data-testid={`${testPrefix}-followup-use-days-${i}`}>
                  Use a delay instead
                </button>
              </>
            ) : (
              <>
                <input className="vav-session-input" type="number" min="0" max="365"
                  style={{ width: 80 }}
                  value={f.after_days ?? 14}
                  onChange={(e) => update(i, { after_days: parseInt(e.target.value, 10) || 0 })}
                  data-testid={`${testPrefix}-followup-days-${i}`} />
                <span>day{(f.after_days ?? 14) === 1 ? '' : 's'} after the session</span>
                <button className="vav-add-block"
                  onClick={() => update(i, { on_date: new Date().toISOString().slice(0, 10) })}
                  data-testid={`${testPrefix}-followup-use-date-${i}`}>
                  Use a fixed date instead
                </button>
              </>
            )}
          </div>

          <input className="vav-session-input"
            placeholder="Subject — e.g. How are things settling, {client_name}?"
            value={f.subject || ''}
            onChange={(e) => update(i, { subject: e.target.value })}
            data-testid={`${testPrefix}-followup-subject-${i}`} />
          <textarea className="vav-session-input"
            style={{ minHeight: 80, resize: 'vertical', lineHeight: 1.5,
                     fontFamily: "'Source Sans 3', sans-serif" }}
            placeholder="Your message. Nothing is sent if this is empty."
            value={f.text || ''}
            onChange={(e) => update(i, { text: e.target.value })}
            data-testid={`${testPrefix}-followup-text-${i}`} />
        </div>
      ))}

      <button className="vav-add-block" onClick={add} data-testid={`${testPrefix}-followup-add`}>
        <Plus size={11} /> Add {list.length ? 'another ' : 'a '}follow-up email
      </button>
    </div>
  );
}
